/**
 * Pure image-format helpers (no native dependencies).
 *
 * Shared by the image picker and the upload validation preflight so both agree
 * on what counts as an unsupported RAW capture. Kept free of native modules so
 * it stays trivially testable and importable from any layer.
 */

/** RAW capture container extensions we cannot decode or upload. */
export const RAW_IMAGE_EXTENSIONS = [
  'dng',
  'raw',
  'arw',
  'nef',
  'cr2',
  'cr3',
  'orf',
  'rw2',
] as const;

export interface ImageFormatMeta {
  mimeType?: string | null;
  fileName?: string | null;
}

/**
 * Whether a picked asset is a RAW capture (e.g. a Pixel/DSLR `.dng`).
 *
 * RAW files are not decodable by the image pipeline and are rejected by the
 * server's MIME allowlist, so we surface a friendly message up front rather
 * than letting them fail deep in the upload.
 */
export function isRawAsset({ mimeType, fileName }: ImageFormatMeta): boolean {
  const extension = fileName?.toLowerCase().split('.').pop();
  if (extension && (RAW_IMAGE_EXTENSIONS as readonly string[]).includes(extension)) {
    return true;
  }

  const type = mimeType?.toLowerCase() ?? '';
  return type.includes('dng') || type.includes('raw') || type.includes('x-adobe');
}
